function getNativeLivoraCallPlugin() {
  try {
    if (typeof window === "undefined") {
      return null;
    }
    return window.Capacitor?.Plugins?.NativeLivoraCall ?? null;
  } catch {
    return null;
  }
}

/**
 * Read NativeLivoraCall plugin status from Android (logging/diagnostics only).
 * Does not enable native calls or change platform routing.
 */
export async function getNativeLivoraCallStatus() {
  const plugin = getNativeLivoraCallPlugin();
  if (!plugin || typeof plugin.getStatus !== "function") {
    return {
      present: false,
      ready: false,
      available: false,
      reason: "plugin_not_present",
    };
  }

  try {
    const result = await plugin.getStatus();
    return {
      present: result?.present === true,
      ready: result?.ready === true,
      available: result?.available === true,
      reason: String(result?.reason || "unknown"),
      plugin: result?.plugin ? String(result.plugin) : undefined,
      message: result?.message ? String(result.message) : undefined,
    };
  } catch (error) {
    return {
      present: true,
      ready: false,
      available: false,
      reason: "get_status_failed",
      error: String(error?.message || error),
    };
  }
}

/**
 * Open LivoraCallActivity shell via native plugin (diagnostics/dev bridge only).
 * Does not start a native call, Zego engine, camera, or mic.
 */
export async function openNativeLivoraCallShell() {
  const plugin = getNativeLivoraCallPlugin();
  if (!plugin || typeof plugin.openShell !== "function") {
    return {
      opened: false,
      present: false,
      reason: "plugin_not_present",
    };
  }

  try {
    const result = await plugin.openShell();
    return {
      opened: result?.opened === true,
      present: true,
      phase: result?.phase ? String(result.phase) : undefined,
      shellOnly: result?.shellOnly === true,
      reason: result?.reason ? String(result.reason) : undefined,
    };
  } catch (error) {
    return {
      opened: false,
      present: true,
      reason: "open_shell_failed",
      error: String(error?.message || error),
    };
  }
}

/**
 * Open LivoraZegoNativeCaptureTestActivity (Phase D isolated proof) via native plugin.
 * Generated test pattern only, disposable test room, no camera, no production
 * call room, no billing/heartbeat. Fully isolated from CallRoomPage.
 */
export async function openZegoNativeCaptureTest() {
  const plugin = getNativeLivoraCallPlugin();
  if (!plugin || typeof plugin.openZegoNativeCaptureTest !== "function") {
    return {
      opened: false,
      present: false,
      reason: "plugin_not_present",
    };
  }

  try {
    const result = await plugin.openZegoNativeCaptureTest();
    return {
      opened: result?.opened === true,
      present: true,
      phase: result?.phase ? String(result.phase) : undefined,
      reason: result?.reason ? String(result.reason) : undefined,
    };
  } catch (error) {
    return {
      opened: false,
      present: true,
      reason: "open_zego_native_capture_test_failed",
      error: String(error?.message || error),
    };
  }
}

/**
 * Open LivoraZegoNativeRealCameraTestActivity (Phase 3C isolated proof) via
 * native plugin. Zego built-in real camera + microphone capture only,
 * disposable test room, no Snap, no filters, no billing/heartbeat. Fully
 * isolated from CallRoomPage and LivoraNativeCallActivity.
 */
export async function openZegoNativeRealCameraTest() {
  const plugin = getNativeLivoraCallPlugin();
  if (!plugin || typeof plugin.openZegoNativeRealCameraTest !== "function") {
    return {
      opened: false,
      present: false,
      reason: "plugin_not_present",
    };
  }

  try {
    const result = await plugin.openZegoNativeRealCameraTest();
    return {
      opened: result?.opened === true,
      present: true,
      phase: result?.phase ? String(result.phase) : undefined,
      reason: result?.reason ? String(result.reason) : undefined,
    };
  } catch (error) {
    return {
      opened: false,
      present: true,
      reason: "open_zego_native_real_camera_test_failed",
      error: String(error?.message || error),
    };
  }
}

/**
 * Open LivoraSnapZegoFilteredCaptureTestActivity (Phase 3E-3 isolated proof)
 * via native plugin. Snap Camera Kit owns the camera and applies a real lens;
 * filtered frames are converted to I420 and fed into Zego's custom video
 * capture to publish/play between two devices. Zego's built-in camera capture
 * is never used. Test-only, disposable, isolated - no billing, no production
 * call room.
 */
export async function openSnapZegoFilteredCaptureTest() {
  const plugin = getNativeLivoraCallPlugin();
  if (!plugin || typeof plugin.openSnapZegoFilteredCaptureTest !== "function") {
    return {
      opened: false,
      present: false,
      reason: "plugin_not_present",
    };
  }

  try {
    const result = await plugin.openSnapZegoFilteredCaptureTest();
    return {
      opened: result?.opened === true,
      present: true,
      phase: result?.phase ? String(result.phase) : undefined,
      reason: result?.reason ? String(result.reason) : undefined,
    };
  } catch (error) {
    return {
      opened: false,
      present: true,
      reason: "open_snap_zego_filtered_capture_test_failed",
      error: String(error?.message || error),
    };
  }
}

/**
 * Phase 4A-1 skeleton: open LivoraNativeCallRoomActivity via native plugin
 * with the real call data contract. No Snap/Zego video wiring yet - proves
 * the React route -> plugin -> Activity handoff only. Does not touch
 * CallRoomPage.js, heartbeat, billing, or Firebase Functions.
 *
 * @param {{
 *   callId: string,
 *   roomId: string,
 *   userId: string,
 *   userName?: string,
 *   role: string,
 *   otherUserId: string,
 *   ownStreamId: string,
 *   zegoAppId: string,
 *   zegoToken: string,
 * }} params
 */
export async function openNativeCallRoom(params) {
  const plugin = getNativeLivoraCallPlugin();
  if (!plugin || typeof plugin.openNativeCallRoom !== "function") {
    return {
      opened: false,
      present: false,
      reason: "plugin_not_present",
    };
  }

  try {
    const result = await plugin.openNativeCallRoom(params || {});
    return {
      opened: result?.opened === true,
      present: true,
      phase: result?.phase ? String(result.phase) : undefined,
      reason: result?.reason ? String(result.reason) : undefined,
    };
  } catch (error) {
    return {
      opened: false,
      present: true,
      reason: "open_native_call_room_failed",
      error: String(error?.message || error),
    };
  }
}

/**
 * Phase N2A section 5: call on real web logout / account-switch only -
 * never when a single call Activity finishes. Clears every prepared native
 * session and signs Android's FirebaseAuth out, so a stale session can
 * never survive into a different account on this device.
 */
export async function clearNativeCallSessionForLogout() {
  const plugin = getNativeLivoraCallPlugin();
  if (!plugin || typeof plugin.clearNativeCallSessionForLogout !== "function") {
    return { cleared: false, present: false, reason: "plugin_not_present" };
  }
  try {
    const result = await plugin.clearNativeCallSessionForLogout();
    return { cleared: result?.cleared === true, present: true };
  } catch (error) {
    return {
      cleared: false,
      present: true,
      reason: "clear_native_call_session_failed",
      error: String(error?.message || error),
    };
  }
}

/**
 * Phase N2A1 section 2: immediate, stateless probe of the live native
 * FirebaseAuth session. Never returns or logs a token. Use this FIRST -
 * if uidMatch is true, do not call createNativeCallSession or
 * prepareNativeCall at all.
 *
 * @param {{ expectedUid: string }} params
 */
export async function getNativeCallAuthState(params) {
  const plugin = getNativeLivoraCallPlugin();
  if (!plugin || typeof plugin.getNativeCallAuthState !== "function") {
    return { present: false, ready: false, uidMatch: false, reason: "plugin_not_present" };
  }
  try {
    const result = await plugin.getNativeCallAuthState(params || {});
    return {
      present: result?.present === true,
      ready: result?.ready === true,
      uidMatch: result?.uidMatch === true,
    };
  } catch (error) {
    return {
      present: false,
      ready: false,
      uidMatch: false,
      reason: "get_native_call_auth_state_failed",
      error: String(error?.message || error),
    };
  }
}

/**
 * Phase N2A1 section 3: only call this after getNativeCallAuthState already
 * found no matching native user. Hands a native Firebase custom token +
 * expected uid to NativeCallStartupCoordinator, which performs a real
 * sign-in and resolves this promise only from FirebaseAuth's actual
 * completion (never merely because the request was dispatched). Concurrent
 * calls for the same expectedUid are deduped natively.
 *
 * @param {{ callId: string, expectedUid: string, nativeFirebaseCustomToken: string, role?: string }} params
 */
export async function prepareNativeCall(params) {
  const plugin = getNativeLivoraCallPlugin();
  if (!plugin || typeof plugin.prepareNativeCall !== "function") {
    return { prepared: false, authReady: false, uidMatch: false, present: false, reason: "plugin_not_present" };
  }

  try {
    const result = await plugin.prepareNativeCall(params || {});
    return {
      prepared: result?.prepared === true,
      authReady: result?.authReady === true,
      uidMatch: result?.uidMatch === true,
      present: true,
      reason: result?.reason ? String(result.reason) : undefined,
    };
  } catch (error) {
    return {
      prepared: false,
      authReady: false,
      uidMatch: false,
      present: true,
      reason: "prepare_native_call_failed",
      error: String(error?.message || error),
    };
  }
}

/**
 * Phase N2A1 section 5: clears only this callId's prepared native-auth
 * entry (reject/cancel/timeout/terminal-before-accept/accept failure/open
 * failure) - never signs out a matching account session.
 *
 * @param {{ callId: string, expectedUid: string }} params
 */
export async function clearPreparedNativeCall(params) {
  const plugin = getNativeLivoraCallPlugin();
  if (!plugin || typeof plugin.clearPreparedNativeCall !== "function") {
    return { cleared: false, present: false, reason: "plugin_not_present" };
  }
  try {
    const result = await plugin.clearPreparedNativeCall(params || {});
    return { cleared: result?.cleared === true, present: true };
  } catch (error) {
    return {
      cleared: false,
      present: true,
      reason: "clear_prepared_native_call_failed",
      error: String(error?.message || error),
    };
  }
}

/**
 * Phase N2A: pushes the other participant's display name/photo into an
 * already-open LivoraNativeCallRoomActivity, resolved after the Activity
 * opened so it never blocks Activity visibility or first video frame.
 *
 * @param {{ callId: string, otherUserName: string, otherUserPhotoUrl: string }} params
 */
export async function updateNativeCallRoomProfile(params) {
  const plugin = getNativeLivoraCallPlugin();
  if (!plugin || typeof plugin.updateNativeCallRoomProfile !== "function") {
    return { updated: false, present: false, reason: "plugin_not_present" };
  }

  try {
    const result = await plugin.updateNativeCallRoomProfile(params || {});
    return {
      updated: result?.updated === true,
      present: true,
    };
  } catch (error) {
    return {
      updated: false,
      present: true,
      reason: "update_native_call_room_profile_failed",
      error: String(error?.message || error),
    };
  }
}

/**
 * Phase 4B: subscribe to the native "nativeCallEnded" event, fired when
 * LivoraNativeCallRoomActivity closes (End button, back press, or system
 * destroy). Returns a handle with .remove() to unsubscribe; if the plugin
 * isn't present, returns a no-op handle so callers never need to branch.
 *
 * @param {(payload: {callId: string, reason: string}) => void} callback
 */
export function addNativeCallEndedListener(callback) {
  const plugin = getNativeLivoraCallPlugin();
  if (!plugin || typeof plugin.addListener !== "function") {
    return { remove: async () => {} };
  }

  return plugin.addListener("nativeCallEnded", (data) => {
    callback({
      callId: data?.callId ? String(data.callId) : "",
      reason: data?.reason ? String(data.reason) : "unknown",
    });
  });
}

/**
 * Phase 4D: native Firestore saw calls/{callId} become terminal while the
 * Activity is open (peer/server end). Does NOT imply local endCall.
 *
 * @param {(payload: {
 *   callId: string,
 *   status: string,
 *   endedReason: string,
 *   endedBy: string,
 *   source: string,
 * }) => void} callback
 */
export function addNativeCallRemoteTerminalListener(callback) {
  const plugin = getNativeLivoraCallPlugin();
  if (!plugin || typeof plugin.addListener !== "function") {
    return { remove: async () => {} };
  }

  return plugin.addListener("nativeCallRemoteTerminal", (data) => {
    callback({
      callId: data?.callId ? String(data.callId) : "",
      status: data?.status ? String(data.status) : "",
      endedReason: data?.endedReason ? String(data.endedReason) : "",
      endedBy: data?.endedBy ? String(data.endedBy) : "",
      source: data?.source ? String(data.source) : "native_firestore",
    });
  });
}

/**
 * Phase N1.1: instant remote-hangup hint delivered via Zego point-to-point
 * signaling (validated native-side against room/callId/expected sender
 * before this ever fires). Firebase/Firestore remains authoritative -
 * this is a fast UI-teardown signal only, never a billing/status source.
 *
 * @param {(payload: {
 *   callId: string,
 *   source: string,
 *   senderUid: string,
 *   reason: string,
 * }) => void} callback
 */
export function addNativeCallRemoteHangupSignalListener(callback) {
  const plugin = getNativeLivoraCallPlugin();
  if (!plugin || typeof plugin.addListener !== "function") {
    return { remove: async () => {} };
  }

  return plugin.addListener("nativeCallRemoteHangupSignal", (data) => {
    callback({
      callId: data?.callId ? String(data.callId) : "",
      source: data?.source ? String(data.source) : "zego_custom_command",
      senderUid: data?.senderUid ? String(data.senderUid) : "",
      reason: data?.reason ? String(data.reason) : "",
    });
  });
}

/**
 * Phase 4C-1: tells the currently-open LivoraNativeCallRoomActivity (if any)
 * to close itself, used when React's own live Firestore listener already
 * knows the call is terminal (e.g. the other side ended it). Does not call
 * endCall or touch billing/heartbeat - purely closes the native screen.
 *
 * @param {{ callId: string, reason: string }} params
 */
export async function closeNativeCallRoom(params) {
  const plugin = getNativeLivoraCallPlugin();
  if (!plugin || typeof plugin.closeNativeCallRoom !== "function") {
    return {
      closed: false,
      present: false,
      reason: "plugin_not_present",
    };
  }

  try {
    const result = await plugin.closeNativeCallRoom(params || {});
    return {
      closed: result?.closed === true,
      present: true,
      phase: result?.phase ? String(result.phase) : undefined,
    };
  } catch (error) {
    return {
      closed: false,
      present: true,
      reason: "close_native_call_room_failed",
      error: String(error?.message || error),
    };
  }
}
